import React, { useState } from 'react';
import { ShieldCheck, Target, Lock, User, Dumbbell } from 'lucide-react';

export default function Onboarding({ user, updateUserSettings, updateTrainingDays, api, token, btnClass, onComplete }) {
    const [age, setAge] = useState(user?.age || 30);
    const [level, setLevel] = useState(user?.level || 'Beginner');
    const [location, setLocation] = useState(user?.training_location || 'gym');
    
    // Focus pakken we default uit wat de user bij registratie koos.
    const [focus, setFocus] = useState(user?.training_type || 'Crossfit');
    const [days, setDays] = useState(user?.training_days_per_week || 4);
    const [pwd, setPwd] = useState('');
    const [loading, setLoading] = useState(false);

    const hasMinLen = pwd.length >= 12;
    const hasUpper = /[A-Z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
    const isPwdValid = hasMinLen && hasUpper && hasNumber && hasSpecial;
    
    const isValid = isPwdValid && age > 0;

    // Als de atleet specifieke toegewezen programma's heeft (> 1), toon dan de dropdown. Anders verbergen we hem.
    const showFocusDropdown = Array.isArray(user?.allowed_programs) && user.allowed_programs.length > 1;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValid || loading) return;
        setLoading(true);
        try {
            await api.fetch('/profile/password', { method: 'PUT', body: JSON.stringify({ newPassword: pwd }) }, token);
            await updateTrainingDays(days);
            await updateUserSettings({
                age: parseInt(age),
                level: level,
                training_location: location,
                training_type: focus
            });
            onComplete();
        } catch (err) {
            alert('Fout tijdens opslaan: ' + err.message);
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-md flex items-center justify-center p-4 z-[999999] overflow-y-auto">
            <div className="bg-zinc-900 border border-orange-500/30 shadow-[0_0_40px_rgba(234,88,12,0.15)] rounded-2xl p-6 md:p-8 w-full max-w-2xl my-8 relative">
                
                {/* Branding Header */}
                <div className="flex flex-col items-center gap-3 mb-8">
                    <img 
                        src="/logo192.png" 
                        alt="MyWoD Logo" 
                        className="w-20 h-20 object-contain drop-shadow-xl" 
                        onError={(e) => { e.target.outerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#f97316" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>`}} 
                    />
                    <div className="text-center">
                        <h2 className="text-3xl sm:text-4xl font-black text-white uppercase tracking-wider font-logo">MYWOD</h2>
                        <p className="text-orange-500 text-xs sm:text-sm font-bold uppercase tracking-widest mt-1">Laten we je profiel afmaken</p>
                        <p className="text-gray-400 text-sm mt-3 max-w-sm mx-auto">Beveilig je account met een nieuw wachtwoord en stel je doelen in voordat je de arena instapt.</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* Fysiek & Ervaring */}
                        <div className="space-y-4 bg-black/40 p-5 rounded-xl border border-zinc-700/50 shadow-inner">
                            <h3 className="text-orange-500 font-bold uppercase tracking-widest text-xs flex items-center gap-2 mb-4">
                                <User size={14}/> Fysiek & Ervaring
                            </h3>
                            <div>
                                <label className="text-xs text-gray-400 block mb-1">Leeftijd</label>
                                <input type="number" min="10" max="100" value={age} onChange={(e) => setAge(e.target.value)} className="w-full bg-black/60 border border-zinc-700 p-3 rounded-lg text-white focus:border-orange-500 transition-colors outline-none" required />
                            </div>
                            <div>
                                <label className="text-xs text-gray-400 block mb-1">Ervaringsniveau</label>
                                <select value={level} onChange={(e) => setLevel(e.target.value)} className="w-full bg-black/60 border border-zinc-700 p-3 rounded-lg text-white focus:border-orange-500 transition-colors outline-none">
                                    <option value="Beginner">Beginner</option>
                                    <option value="Gevorderd">Gevorderd</option>
                                    <option value="Expert">Expert</option>
                                    <option value="Elite">Elite</option>
                                </select>
                            </div>
                        </div>

                        {/* Omgeving (& Optioneel Focus) */}
                        <div className="space-y-4 bg-black/40 p-5 rounded-xl border border-zinc-700/50 shadow-inner">
                            <h3 className="text-orange-500 font-bold uppercase tracking-widest text-xs flex items-center gap-2 mb-4">
                                <Target size={14}/> Doel & Omgeving
                            </h3>
                            
                            {showFocusDropdown && (
                                <div>
                                    <label className="text-xs text-gray-400 block mb-1">Primaire Focus</label>
                                    <select value={focus} onChange={(e) => setFocus(e.target.value)} className="w-full bg-black/60 border border-zinc-700 p-3 rounded-lg text-white focus:border-orange-500 transition-colors outline-none">
                                        {user.allowed_programs.map(prog => (
                                            <option key={prog} value={prog}>{prog}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="text-xs text-gray-400 block mb-1">Waar train je het meest?</label>
                                <select value={location} onChange={(e) => setLocation(e.target.value)} className="w-full bg-black/60 border border-zinc-700 p-3 rounded-lg text-white focus:border-orange-500 transition-colors outline-none">
                                    <option value="gym">Commercial Gym / Box</option>
                                    <option value="home">Home Gym</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Trainingsdoel */}
                    <div className="bg-black/40 p-5 rounded-xl border border-zinc-700/50 shadow-inner">
                        <h3 className="text-orange-500 font-bold uppercase tracking-widest text-xs flex items-center gap-2 mb-4">
                            <Dumbbell size={14}/> Trainingsdoel (Dagen per week: {days})
                        </h3>
                        <input type="range" min="1" max="7" value={days} onChange={(e) => setDays(parseInt(e.target.value))} className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-orange-500"/>
                        <div className="flex justify-between text-xs text-gray-500 mt-2 font-bold">
                            <span>1 dag</span>
                            <span>7 dagen</span>
                        </div>
                    </div>

                    {/* Wachtwoord Wijziging */}
                    <div className="bg-black/40 p-5 rounded-xl border border-zinc-700/50 shadow-inner space-y-4">
                        <h3 className="text-red-500 font-bold uppercase tracking-widest text-xs flex items-center gap-2 mb-4">
                            <Lock size={14}/> Veiligheid (Nieuw Wachtwoord)
                        </h3>
                        <div>
                            <input type="password" placeholder="Kies een veilig wachtwoord" value={pwd} onChange={(e) => setPwd(e.target.value)} className="w-full p-4 bg-black/60 rounded-xl text-white border border-zinc-700 focus:border-red-500 transition-colors outline-none" required />
                        </div>
                        {pwd.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                                <div className={`text-xs flex items-center gap-2 ${hasMinLen ? 'text-green-500' : 'text-gray-500'}`}><ShieldCheck size={14}/> Minimaal 12 tekens</div>
                                <div className={`text-xs flex items-center gap-2 ${hasUpper ? 'text-green-500' : 'text-gray-500'}`}><ShieldCheck size={14}/> Minimaal 1 hoofdletter</div>
                                <div className={`text-xs flex items-center gap-2 ${hasNumber ? 'text-green-500' : 'text-gray-500'}`}><ShieldCheck size={14}/> Minimaal 1 cijfer</div>
                                <div className={`text-xs flex items-center gap-2 ${hasSpecial ? 'text-green-500' : 'text-gray-500'}`}><ShieldCheck size={14}/> Minimaal 1 speciaal teken (!@#$)</div>
                            </div>
                        )}
                    </div>

                    <button type="submit" disabled={!isValid || loading} className={`w-full py-4 text-lg bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white rounded-xl shadow-[0_0_15px_rgba(234,88,12,0.3)] transition-all font-black tracking-widest uppercase ${(!isValid || loading) ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}>
                        {loading ? 'Bezig met opslaan...' : 'OPSLAAN & STARTEN'}
                    </button>
                </form>
            </div>
        </div>
    );
}
